import type { FieldParameter } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import {
  getFieldKey,
  getResourceKinds,
  getResourceType,
} from "@fartherfinance/frontend/api/TaskWorkflows/utils/fieldHelpers";
import { hasEnumData } from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";
import type { AdvisorRequestConfig } from "@fartherfinance/frontend/api/Types";

import { isResourceTypeWithValue } from "../../core/types";
import {
  getResourceResolver,
  getResourceResolverWithKind,
} from "../resources/resourcesRegistry";

import { batchSearchParameters } from "./batchSearchParameters";

import type { EnumOption, ResourceTypeResponse } from "../../core/types";
import type { UseFormGetValues } from "react-hook-form";

const getAppropriateResolver = (fieldParameter: FieldParameter) => {
  try {
    const resourceType = getResourceType(fieldParameter);
    if (!resourceType) {
      return null;
    }

    const resourceTypeName = resourceType.type;
    const kinds = getResourceKinds(fieldParameter);
    const kindString = kinds.length > 0 ? String(kinds[0]) : "";

    if (kindString) {
      return getResourceResolverWithKind(resourceTypeName, kindString);
    }

    return getResourceResolver(resourceTypeName);
  } catch (e) {
    console.error(`[fieldOptionsService] Error getting resolver:`, e);
    const resourceType = getResourceType(fieldParameter);
    return resourceType ? getResourceResolver(resourceType.type) : null;
  }
};

type BatchFieldOptionsResult = Record<string, EnumOption[]>;

export const fetchFieldOptionsBatch = async (
  fieldParameters: FieldParameter[],
  auth: AdvisorRequestConfig | null,
  getValues?: UseFormGetValues<Record<string, unknown>>
): Promise<BatchFieldOptionsResult> => {
  const enumFields = fieldParameters.filter((param) => hasEnumData(param));

  if (enumFields.length === 0 || !auth) {
    return {};
  }

  try {
    const response = await batchSearchParameters(enumFields, auth, getValues);
    const result: BatchFieldOptionsResult = {};

    for (const field of enumFields) {
      const key = getFieldKey(field);
      const resourceType = getResourceType(field);

      if (!response.data[key] || !resourceType) {
        result[key] = [];
        continue;
      }

      if (resourceType.type === "StaticEnum") {
        const staticOptions =
          response.data[key]?.values
            .map((v) =>
              typeof v === "object" &&
              v !== null &&
              "id" in v &&
              "displayValue" in v
                ? {
                    type: "StaticEnum",
                    value: v.id,
                    label: v.displayValue ?? "",
                  }
                : null
            )
            .filter((v): v is NonNullable<typeof v> => v !== null) || [];

        result[key] = staticOptions;
      } else if (resourceType.type === "TextEnum") {
        const staticOptions =
          response.data[key]?.values.map((v) => ({
            value: typeof v === "string" ? v : String(v),
            label: typeof v === "string" ? v : String(v),
          })) || [];
        result[key] = staticOptions as EnumOption[];
      } else {
        try {
          const resolver = getAppropriateResolver(field);

          if (!resolver) {
            result[key] = [];
            continue;
          }

          const rawValues = response.data[key]?.values || [];
          const values: ResourceTypeResponse[] = Array.isArray(rawValues)
            ? rawValues.filter(isResourceTypeWithValue)
            : [];

          const options = await resolver(auth, values);
          result[key] = options;
        } catch (error) {
          console.error(
            `[fieldOptionsService] Error resolving options for ${key}:`,
            error
          );
          result[key] = [];
        }
      }
    }

    return result;
  } catch (error) {
    console.error("[fieldOptionsService] Error in batch fetch:", error);
    const result: BatchFieldOptionsResult = {};
    enumFields.forEach((field) => {
      result[getFieldKey(field)] = [];
    });
    return result;
  }
};
